import { toggleElementVisibility } from '@bark/bark-core/src/helpers/dom-manipulation';
import {
	_t,
	BuyerProjectInteractionTracking,
	useNamespace,
} from '@bark/bark-core';

let state = {
	hasCompletedRQFA: false,
	canCompleteRQFA: true,
	sellersAbleToHaveQuoteRequested: [],
	requestReplyModal: null,
	experiments: [],
	sortedBy: 'matches',
};

/**
 *
 * @param sellerList list of sellers -- these wil lbe checked to see if they exist / have been quoted
 * @param projectId project ID for submitting acitons against
 * @param project Project object holds experiment data and whether or not the project already had quote from all requested
 * @param buyer Buyer is needed for submitting events
 * @param p Current page, used in some views and impacts whether or not to show the RQfA
 * @param lt Current List Tab, used in some views
 * @param ls List sort -- RQfA will change depending on the sort.
 * @param filterParams Filter param -- will always be an empty object unless in filters exp where it would contain the filters applied.
 */
export function handleShowingRQFA({
	sellerList,
	projectId,
	project,
	buyer,
	p,
	lt,
	ls,
	filterParams,
}) {
	if (
		getShouldShowRequestQuoteFromAll({ project, sellerList, p, lt, ls, filterParams })
	) {
		state.sortedBy = ls;

		if (window.BuyerApp) {
			const options = {
				hashedProjectId: projectId,
				bpvId: project.bpvid,
				matchedSellerIds: sellerList
					.filter(
						(seller) =>
							seller.attributes.quote_status === null
					)
					.slice(0, 5)
					.map((e) => e.id),
				buyerPhone: buyer?.data?.telephone,
				sortType: ls,
				trackingData: getTrackingData(state.sellersAbleToHaveQuoteRequested, sellerList),
				onSuccess: markQuoteRequested,
				// onError
			};

			//unmount before launching so they reset
			window.BuyerApp.unmountModule('__reqQfromAllBtn');
			window.BuyerApp.unmountModule('__reqQfromAllMidBtn');
			window.BuyerApp.unmountModule('__reqQfromAllBottomBtn');

			window.BuyerApp.launchModule(
				'request-multiple-replies',
				'__reqQfromAllBtn',
				options,
			);
			window.BuyerApp.launchModule(
				'request-multiple-replies',
				'__reqQfromAllMidBtn',
				options,
			);
			window.BuyerApp.launchModule(
				'request-multiple-replies',
				'__reqQfromAllBottomBtn',
				options,
			);
		}

		toggleRequestQuoteFromAllRow(true);
		handleSplitListViewChanges(ls, sellerList);
		bindRequestQuoteFromAllListeners({
			projectId,
			sellerList,
			bpvid: project.bpvid ?? null,
		});
		rerender(getQuoteableSellerCount());
	} else {
		toggleRequestQuoteFromAllRow(false);
	}
}

/** @param shouldNotRequestQuoteFromAll boolean pass in truthy value to disable RQfA */
export function disableBulkRequestQuote(shouldNotRequestQuoteFromAll) {
	state.canCompleteRQFA = !shouldNotRequestQuoteFromAll;
}

export function setQuoteRequested($sellerRow) {
	useNamespace(['buyer_seller-request-quote']).then(function () {
		updateQuoteLinks($sellerRow);
		updateQuoteButtons($sellerRow);
		removeSellerFromQuotableSellers($sellerRow.data('seller-profile-id'));
		handleSplitListViewChanges();
	});
}

const toggleRequestQuoteFromAllRow = (show) => {
	if (!show) {
		$('.rqfa-container.collapse').collapse('hide');
		$('.rqfa-container.animate-in').removeClass('animate-in');
	}
	toggleElementVisibility('.js-request-quotes-row', show);

	//In Filters exp
	if ($('.filters-rqfa-top').length > 0) {
		if (show) {
			$('.filters-rqfa-top').addClass('show-border');
		} else {
			$('.filters-rqfa-top').removeClass('show-border');
		}
	}
};

const getShouldShowRequestQuoteFromAll = ({
	project,
	sellerList,
	p,
	lt,
	ls,
	filterParams,
}) => {
	return (
		!getRQFACompleted() &&
		!getBulkRequestQuoteDisabled() &&
		checkProjectShouldShowRQFA(project) &&
		checkSellersShouldShowRQFA(sellerList) &&
		checkSplitParamsShouldShowRQFA({ p, lt }) &&
		checkFilterParamsShouldShowRQFA(filterParams)
	);
};

const checkSellersShouldShowRQFA = (sellerList) => {
	if (sellerList.length > 0) {
		setSellersAbleToHaveQuoteRequested(sellerList);
		return getQuoteableSellerCount() > 0;
	}
	return false;
};

const checkProjectShouldShowRQFA = (project) => {
	let projectState = project?.state;
	if (
		project.has_requested_quote_from_all ||
		!projectState?.is_active ||
		!projectState?.is_open ||
		projectState?.rejected ||
		projectState?.is_old
	) {
		return false;
	}
	return true;
};

/**
 *  Checks if on first page and on matches tab
 * @param p
 * @param lt
 * @returns {boolean}
 */
const checkSplitParamsShouldShowRQFA = ({ p, lt }) => {
	return (!p || p === 1) && (!lt || lt === 'matches');
};

const checkFilterParamsShouldShowRQFA = (filterParams) => {
	let filterValues = Object.values(filterParams);
	return filterValues.every((val) => {
		return val === 'all';
	});
};

// how many in the top 5 have not yet had a quote request
const setSellersAbleToHaveQuoteRequested = (sellerList) => {
	state.sellersAbleToHaveQuoteRequested = sellerList
		.filter(
			(seller) =>
				seller.attributes.quote_status === null,
		)
		.slice(0, 5)
		.map((seller) => seller.id);
};

const getQuoteableSellerCount = () => {
	return state.sellersAbleToHaveQuoteRequested?.length ?? 0;
};

const getRQFACompleted = () => {
	return !!state.hasCompletedRQFA;
};

const getBulkRequestQuoteDisabled = () => {
	return !state.canCompleteRQFA;
};

const handleSplitListViewChanges = (sortParam, sellerList) => {
	const quoteableSellerCount = getQuoteableSellerCount();
	if (sellerList && sellerList.length < 5) {
		$('.rqfa-container.rqfa-lower').remove();
	}
	// only update text/animation if we have been passed in the extra data
	if (sortParam) {
		modifyTextBasedOnSort(quoteableSellerCount, sortParam);
		toggleShowingAnimation(QS.getValue('vis_spidh', false));
	}
	rerender(quoteableSellerCount);

	$('.js-top-matches-count').text(quoteableSellerCount);
	$('.js-top-quotes-string').text(
		_t('buyer_seller-request-quote:quotes', { count: quoteableSellerCount }),
	);
	$('.js-top-matches-string').text(
		_t('buyer_seller-request-quote:matches', { count: quoteableSellerCount }),
	);
};

const modifyTextBasedOnSort = (quoteableSellerCount, sortParam = 'best') => {
	switch (sortParam) {
		case 'review':
			$('.js-rqfa-compare-header').html(
				_t('buyer_seller-request-quote:request-replies-from.top-matches-by-rated', {
					bold_span: "<span class='font-weight-medium'>",
					end_span: '</span>',
					count: quoteableSellerCount,
					interpolation: { escapeValue: false },
				}),
			);
			$('.js-rqfa-compare-header-small').html(
				_t('buyer_seller-request-quote:request-replies-from.top-matches-by-rated', {
					bold_span: "<span class='font-weight-medium'>",
					end_span: '</span>',
					count: quoteableSellerCount,
					interpolation: { escapeValue: false },
				}),
			);
			break;
		case 'resp_time':
			$('.js-rqfa-compare-header').html(
				_t('buyer_seller-request-quote:request-replies-from.top-matches-by-resp-time', {
					bold_span: "<span class='font-weight-medium'>",
					end_span: '</span>',
					count: quoteableSellerCount,
					interpolation: { escapeValue: false },
				}),
			);
			$('.js-rqfa-compare-header-small').html(
				_t('buyer_seller-request-quote:request-replies-from.top-matches-by-resp-time', {
					bold_span: "<span class='font-weight-medium'>",
					end_span: '</span>',
					count: quoteableSellerCount,
					interpolation: { escapeValue: false },
				}),
			);
			break;
		case 'distance':
			$('.js-rqfa-compare-header').html(
				_t('buyer_seller-request-quote:request-replies-from.top-matches-by-distance', {
					bold_span: "<span class='font-weight-medium'>",
					end_span: '</span>',
					count: quoteableSellerCount,
					interpolation: { escapeValue: false },
				}),
			);
			$('.js-rqfa-compare-header-small').html(
				_t('buyer_seller-request-quote:request-replies-from.top-matches-by-distance', {
					bold_span: "<span class='font-weight-medium'>",
					end_span: '</span>',
					count: quoteableSellerCount,
					interpolation: { escapeValue: false },
				}),
			);
			break;
		default:
			$('.js-rqfa-compare-header').html(
				_t('buyer_seller-request-quote:request-replies-from.top-matches-default', {
					bold_span: "<span class='font-weight-medium'>",
					end_span: '</span>',
					count: quoteableSellerCount,
					interpolation: { escapeValue: false },
				}),
			);
			$('.js-rqfa-compare-header-small').html(
				_t('buyer_seller-request-quote:request-replies-from.top-matches-default', {
					bold_span: "<span class='font-weight-medium'>",
					end_span: '</span>',
					count: quoteableSellerCount,
					interpolation: { escapeValue: false },
				}),
			);
			break;
	}
};

const toggleShowingAnimation = (disableAnimation) => {
	// always animate on large screens
	$('.rqfa-container.d-lg-inline-flex').toggleClass('animate-in', true);
	if (disableAnimation) {
		$('.rqfa-container.collapse').addClass('show');
		QS.deleteValue('vis_spidh', false);
	} else {
		$('.rqfa-container.collapse.show').removeClass('show');
		const collapseTimeout = setTimeout(() => {
			$('.rqfa-container.collapse').collapse('show');
			clearTimeout(collapseTimeout);
		}, 750);
	}
};

const bindRequestQuoteFromAllListeners = ({ projectId, bpvid, sellerList }) => {
	const $reqFromAll = $('.js-request-quotes-from-all');
	$reqFromAll.off('click.rqfa').on('click.rqfa', (e) => {
		e.preventDefault();
		if (
			getRQFACompleted() ||
			getBulkRequestQuoteDisabled() ||
			getQuoteableSellerCount() < 1
		) {
			$reqFromAll.off('click.rqfa');
			toggleRequestQuoteFromAllRow(false);
			bugsnagClient.notify(
				new Error(
					"Request quotes from all link was used when it shouldn't have been available.",
				),
			);
		} else {
			let data = {
				sellerProfileIds: state.sellersAbleToHaveQuoteRequested,
				projectId: projectId,
				bpvId: bpvid,
			};

			const tracking = BuyerProjectInteractionTracking();
			tracking.submitExpActWithVal('seller-list-request-quote-from-all', {
				projectId,
				sortType: QS.getValue('ls', 'best'),
				...getTrackingData(state.sellersAbleToHaveQuoteRequested, sellerList),
			});

			let callbacksData = {
				requestQuoteRqfaSuccess: requestReplyRqfaSuccess,
				requestQuoteRqfaError: requestReplyRqfaError,
			};

			state.requestReplyModal.show(data, callbacksData);
		}
	});

	// todo move this out of RQ module bc its only semi related.  This prevents animation from showing a bunch of times when swiping back from a seller profile on mkobile
	$('#project-seller-list')
		.off('click.rqfav1')
		.on('click.rqfav1', '.js-view-profile-action', function (e) {
			e.preventDefault();
			const $self = $(this);
			const spidh = $self.data('sellerProfileId');
			QS.setValue('vis_spidh', `${spidh}`, true);
		});

	// this is to repeat teh animation whenever the sort is changed. remove this listener to prevent that
	$('#matches-list')
		.off('startSkeleton.rqfa')
		.on('startSkeleton.rqfa', () => toggleRequestQuoteFromAllRow(false));
};

const updateQuoteLinks = ($sellerRow) => {
	$sellerRow.find('div.js-request-quote').each(function () {
		$(this)
			.parent()
			.html(
				'<p class="text-xs mb-1 quote-requested lh-xl">Quote</p><p class="text-xs text-grey-600 mb-0">requested</p>',
			);
		bugsnagClient.notify(
			new Error('We should not be hitting this piece of code anymore.'),
		);
	});
};

const updateQuoteButtons = ($sellerRow) => {
	$sellerRow.find('button.js-request-quote').each(function () {
		let btnText = _t('buyer_seller-request-quote:reply-requested');
		$(this).addClass('disabled').prop('disabled', true).text(btnText);
	});
};

const removeSellerFromQuotableSellers = (sellerProfileId) => {
	if (state.sellersAbleToHaveQuoteRequested) {
		const index = state.sellersAbleToHaveQuoteRequested.indexOf(sellerProfileId);
		if (index >= 0) {
			state.sellersAbleToHaveQuoteRequested.splice(index, 1);
			if (state.sellersAbleToHaveQuoteRequested.length < 1) {
				toggleRequestQuoteFromAllRow(false);
				disableBulkRequestQuote(true);
			}
		}
	}
};

const setExperiments = (experiments) => {
	state.experiments = experiments && Array.isArray(experiments) ? experiments : [];
};

const setRequestReplyModal = (requestReplyModal) => {
	state.requestReplyModal = requestReplyModal;
};

const getExperimentVariant = (experimentName) => {
	let exp = state.experiments.find((item) => item.name === experimentName) || {};
	return exp.variant ?? 0;
};

const markQuoteRequested = (response) => {
	$('.rqfa-container').remove(); // hide all the share with all headers on success
	const sellerArray = [];

	if (Object.keys(response).length) {
		Object.keys(response).forEach((sellerProfileId) => {
			if (response[sellerProfileId].status && sellerProfileId !== 'event_logged') {
				setQuoteRequested(
					$(`.seller-list-card[data-seller-profile-id=${sellerProfileId}]`),
				);
				sellerArray.push(sellerProfileId);
			}
		});
	}

	return sellerArray;
};

const requestReplyRqfaSuccess = (response) => {
	$('.js-request-quotes-from-all').hide();
	toggleRequestQuoteFromAllRow(false);
	disableBulkRequestQuote(true);
	state.hasCompletedRQFA = true;

	const sellerArray = markQuoteRequested(response);
	state.requestReplyModal.setSellerProfileIds(sellerArray);
};

function rerender(quoteableSellerCount) {
	modifyTextBasedOnSort(quoteableSellerCount, state.sortedBy);
	$('.js-request-quotes-from-all').html(
		_t('buyer_seller-request-quote:request-from-top-button.text', {
			count: quoteableSellerCount,
		}),
	);
}

const requestReplyRqfaError = (error) => {
	$('.js-request-quotes-from-all').hide();
	toggleRequestQuoteFromAllRow(false);
	state.requestReplyModal.setSellerProfileIds([]);
};

const getTrackingData = (sellersAbleToHaveQuoteRequested, sellerList) => {
	const sellers = sellersAbleToHaveQuoteRequested.map((sellerId) => {
		const seller = sellerList.find(x => x.id === sellerId);

		const marketplaceMatches = {
			'marketplace-enquiry': 'enquiry',
			'marketplace-booking': 'booking',
			'marketplace-messages': 'messages',
		};
		const matchTypes = seller.attributes?.match_types ?? [];
		const marketplaceCtas = matchTypes.length ?
			matchTypes
				.filter(x => Object.keys(marketplaceMatches).includes(x))
				.map(x => marketplaceMatches[x])
				.join(', ')
			: 'NULL';

		return `${sellerId} - ${marketplaceCtas}`;
	}).join('; ');

	return {
		list: 'all',
		sellers,
	}
}

export const bindRequestQuoteFromAllToWindow = () => {
	window.rqfa = window.rqfa || {};
	window.rqfa.setQuoteRequested = setQuoteRequested;
	window.rqfa.setShouldNotRQFA = disableBulkRequestQuote;
	window.rqfa.handleShowingRQFA = handleShowingRQFA;
	window.rqfa.setExperiments = setExperiments;
	window.rqfa.setRequestReplyModal = setRequestReplyModal;
};
